import React, { useState } from 'react'
import { Box, Grid, TextField, Chip, Autocomplete, Checkbox, FormControlLabel, Button, InputAdornment } from '@mui/material'
import { AccordionSeeker, IconSeeker, ButtonLoadingSeeker, AutocompleteAsyncSeeker, FlagsIconSeeker } from '@ui-seeker/core'
import { SitesMultiSelect } from '@components'
import { BACKEND_APIS } from '../../../@constants/routes'
import {
    filterProductId,
    filterIdSites,
    sitesOptions,
    filterIdSku,
    filterUserId,
    filterAction,
    filterMappingType,
    filterSource,
    filterDateFrom,
    filterDateTo,
    filterQuick,
    excludePropagated,
    sourcesOptions,
    isLoadingTable,
    currentPage,
    clearFilters,
    applyQuickFilter
} from '../@signals'
import { useAuditLog } from '../@hooks'

const ACTION_OPTIONS = [
    { value: 'MAP', label: 'MAP', color: 'success' },
    { value: 'UNMAP', label: 'UNMAP', color: 'error' },
    { value: 'REMAP', label: 'REMAP', color: 'warning' }
]

const MAPPING_TYPE_OPTIONS = [
    { value: 'raw2sku', label: 'raw2sku' },
    { value: 'sku2raw', label: 'sku2raw' },
    { value: 'raw2raw', label: 'raw2raw' },
    { value: 'war2war', label: 'war2war' }
]

const QUICK_FILTER_OPTIONS = [
    { value: 'lastHour', label: 'Last Hour' },
    { value: 'today', label: 'Today' },
    { value: 'thisWeek', label: 'This Week' },
    { value: 'allTime', label: 'All Time' },
    { value: 'none', label: 'Custom' }
]

export default function AuditLogFilters() {
    const [isOpen, setIsOpen] = useState(true)
    const { fetchAuditData } = useAuditLog()

    const handleSearch = () => {
        currentPage.value = 0
        fetchAuditData()
    }

    const handleClear = () => {
        clearFilters()
        fetchAuditData()
    }

    const handleQuickFilter = (value) => {
        applyQuickFilter(value)
        handleSearch()
    }

    const handleActionToggle = (action) => {
        const current = filterAction.value
        if (current.includes(action)) {
            filterAction.value = current.filter(a => a !== action)
        } else {
            filterAction.value = [...current, action]
        }
    }

    const handleMappingTypeToggle = (type) => {
        const current = filterMappingType.value
        if (current.includes(type)) {
            filterMappingType.value = current.filter(t => t !== type)
        } else {
            filterMappingType.value = [...current, type]
        }
    }

    return (
        <AccordionSeeker
            title="Filters"
            expanded={isOpen}
            onChange={() => setIsOpen(!isOpen)}
            TransitionProps={{ unmountOnExit: false }}
        >
            <Box sx={{ p: 2 }}>
                {/* Quick Filters */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <IconSeeker icon="clock" size="sm" />
                        <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>Quick Filters</Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {QUICK_FILTER_OPTIONS.map(opt => (
                            <Chip
                                key={opt.value}
                                label={opt.label}
                                onClick={() => handleQuickFilter(opt.value)}
                                color={filterQuick.value === opt.value ? 'primary' : 'default'}
                                variant={filterQuick.value === opt.value ? 'filled' : 'outlined'}
                                size="small"
                            />
                        ))}
                    </Box>
                </Box>

                <Grid container spacing={2}>
                    {/* Product ID */}
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Product ID"
                            placeholder="Search by product ID"
                            value={filterProductId.value}
                            onChange={(e) => filterProductId.value = e.target.value}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSeeker icon="box" size="sm" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    {/* SKU */}
                    <Grid item xs={12} md={3}>
                        <AutocompleteAsyncSeeker
                            urlAjax={`${BACKEND_APIS.AUDIT_LOG}/skus`}
                            label="SKU"
                            placeholder="Search SKUs..."
                            value={filterIdSku.value}
                            onChange={(e) => filterIdSku.value = e.target.value}
                            queryParam="search"
                            size="small"
                            renderOption={(props, option) => (
                                <li {...props} key={option.value}>
                                    <img
                                        src={option.image_url}
                                        alt=""
                                        style={{ width: 32, height: 32, marginRight: 8, objectFit: 'contain', borderRadius: 4 }}
                                        onError={(e) => { e.target.style.display = 'none' }}
                                    />
                                    <span>{option.label}</span>
                                </li>
                            )}
                        />
                    </Grid>

                    {/* Site */}
                    <Grid item xs={12} md={3}>
                        <SitesMultiSelect
                            options={sitesOptions.value}
                            value={filterIdSites.value}
                            onChange={(newValue) => filterIdSites.value = newValue}
                            label="Site"
                            placeholder="All sites"
                        />
                    </Grid>

                    {/* User */}
                    <Grid item xs={12} md={3}>
                        <AutocompleteAsyncSeeker
                            urlAjax={`${BACKEND_APIS.AUDIT_LOG}/users`}
                            label="User"
                            placeholder="Search users..."
                            value={filterUserId.value}
                            onChange={(e) => filterUserId.value = e.target.value}
                            queryParam="search"
                            size="small"
                        />
                    </Grid>

                    {/* Source */}
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            multiple
                            size="small"
                            options={sourcesOptions.value}
                            getOptionLabel={(option) => option.label || ''}
                            value={filterSource.value}
                            onChange={(_, newValue) => filterSource.value = newValue}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Source"
                                    placeholder="Select sources"
                                />
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={option.value}
                                        label={option.label}
                                        size="small"
                                    />
                                ))
                            }
                        />
                    </Grid>

                    {/* Date Range - only visible when Custom is selected */}
                    {filterQuick.value === 'none' && (
                        <>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="From"
                                    type="date"
                                    value={filterDateFrom.value || ''}
                                    onChange={(e) => filterDateFrom.value = e.target.value || null}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="To"
                                    type="date"
                                    value={filterDateTo.value || ''}
                                    onChange={(e) => filterDateTo.value = e.target.value || null}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </>
                    )}

                    {/* Action Type */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                            <Box component="span" sx={{ fontSize: '0.875rem', color: 'text.secondary', mr: 1 }}>Action:</Box>
                            {ACTION_OPTIONS.map(opt => (
                                <Chip
                                    key={opt.value}
                                    label={opt.value}
                                    onClick={() => handleActionToggle(opt.value)}
                                    color={filterAction.value.includes(opt.value) ? opt.color : 'default'}
                                    variant={filterAction.value.includes(opt.value) ? 'filled' : 'outlined'}
                                    size="small"
                                />
                            ))}
                        </Box>
                    </Grid>

                    {/* Mapping Type */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                            <Box component="span" sx={{ fontSize: '0.875rem', color: 'text.secondary', mr: 1 }}>Type:</Box>
                            {MAPPING_TYPE_OPTIONS.map(opt => (
                                <Chip
                                    key={opt.value}
                                    label={opt.label}
                                    onClick={() => handleMappingTypeToggle(opt.value)}
                                    color={filterMappingType.value.includes(opt.value) ? 'secondary' : 'default'}
                                    variant={filterMappingType.value.includes(opt.value) ? 'filled' : 'outlined'}
                                    size="small"
                                />
                            ))}
                        </Box>
                    </Grid>
                </Grid>

                {/* Additional options */}
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={excludePropagated.value}
                                onChange={(e) => excludePropagated.value = e.target.checked}
                                size="small"
                            />
                        }
                        label="Exclude propagated mappings"
                    />

                    {/* Action buttons */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClear}
                            startIcon={<IconSeeker icon="xmark" size="sm" />}
                        >
                            Clear
                        </Button>
                        <ButtonLoadingSeeker
                            variant="contained"
                            size="small"
                            onClick={handleSearch}
                            loading={isLoadingTable.value}
                            startIcon={<IconSeeker icon="search" size="sm" />}
                        >
                            Search
                        </ButtonLoadingSeeker>
                    </Box>
                </Box>
            </Box>
        </AccordionSeeker>
    )
}
